import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { TreeSelect as AntTreeSelect } from 'antd'
import {
  attempt,
  castArray,
  debounce,
  filter,
  flatten,
  get,
  isArray,
  isEmpty,
  isEqual,
  isUndefined,
  map,
  noop,
  omit,
  size,
  some,
} from 'lodash'
import PropTypes from 'prop-types'

import { usePrevious } from '../../../core/hooks'
import {
  mergeArrayOfObjects,
  stringIncludes,
  uniquePrimitives,
} from '../../../core/utils'

import { SIZES } from '../../constants'
import {
  filterTree,
  handleTreeSelectDropdownSearchHighlights,
} from '../../helpers'
import { antFormItemNamePropType } from '../../prop-types'

import LazyTreeSelectTag from './lazy-tree-select-tag'

const LazyTreeSelect = ({
  name,
  treeData,
  onChange,
  value,
  treeCheckable,
  treeNodeLabelProp,
  setListSize,
  flatTreeData,
  groupedFlatTreeDataByValue,
  onFocus,
  initialValue,
  areMissingChildrenTagsHighlighted,
  ...rest
}) => {
  const [expandedKeys, setExpandedKeys] = useState([])
  const [searchableTreeData, setSearchableTreeData] = useState([])
  const [lazyTreeData, setLazyTreeData] = useState([])
  const [searchValue, setSearchValue] = useState('')

  const initialTreeData = useMemo(() => {
    if (isArray(initialValue)) {
      return map(initialValue, initialValueItem =>
        omit(
          get(
            groupedFlatTreeDataByValue,
            `${get(initialValueItem, 'value', initialValueItem)}[0]`,
          ),
          'children',
        ),
      )
    }

    return castArray(
      omit(get(groupedFlatTreeDataByValue, `${initialValue}[0]`), 'children'),
    )
  }, [groupedFlatTreeDataByValue, initialValue])

  const fullTreeData = useMemo(
    () => (isEmpty(lazyTreeData) ? initialTreeData : lazyTreeData),
    [lazyTreeData, initialTreeData],
  )

  const searchableTreeDataToUse = useMemo(
    () => (isEmpty(searchableTreeData) ? initialTreeData : searchableTreeData),
    [searchableTreeData, initialTreeData],
  )

  const treeDefaultExpandedKeys = useMemo(
    () =>
      isEmpty(searchableTreeData)
        ? undefined
        : uniquePrimitives(
            flatten(
              map(initialValue, initialValueItem =>
                get(
                  groupedFlatTreeDataByValue,
                  `${get(
                    initialValueItem,
                    'value',
                    initialValueItem,
                  )}[0].keyPath`,
                ),
              ),
            ),
          ),
    [groupedFlatTreeDataByValue, initialValue, searchableTreeData],
  )

  const handleDebouncedFilterSearchableTree = useMemo(
    () =>
      debounce(value => {
        const filteredBySearchValueTreeData = filterTree(fullTreeData, node => {
          const nodeLabel = get(node, treeNodeLabelProp)

          if (stringIncludes(value, "'")) {
            return (
              stringIncludes(nodeLabel, value) ||
              stringIncludes(nodeLabel, value.replaceAll("'", '’'))
            )
          }

          return stringIncludes(nodeLabel, value)
        })

        if (!isEmpty(value)) {
          const nodesToExpand = uniquePrimitives(
            flatten(
              map(
                filter(flatTreeData, node => {
                  const nodeLabel = get(node, treeNodeLabelProp)

                  if (stringIncludes(value, "'")) {
                    return (
                      stringIncludes(nodeLabel, value) ||
                      stringIncludes(nodeLabel, value.replaceAll("'", '’'))
                    )
                  }

                  return stringIncludes(nodeLabel, value)
                }),
                'keyPath',
              ),
            ),
          )

          setExpandedKeys(nodesToExpand)
        }

        setSearchableTreeData(filteredBySearchValueTreeData)
      }, 300),
    [fullTreeData, treeNodeLabelProp, flatTreeData],
  )

  const handleOnSearch = useCallback(value => setSearchValue(value), [])

  const handleOnChange = useCallback(
    value => {
      onChange(isUndefined(value) ? null : value)
      attempt(setListSize(size(value)))
    },
    [onChange, setListSize],
  )

  const handleExpandKeys = useCallback(keys => setExpandedKeys(keys), [])

  const handleOnFocusTreeSelectItem = useCallback(
    () => setLazyTreeData(treeData),
    [treeData],
  )

  const nodeIdsAllChildrenSelectedStatus = useMemo(() => {
    if (!areMissingChildrenTagsHighlighted || !treeCheckable) {
      return {}
    }

    return mergeArrayOfObjects(
      map(value, ({ value: selectedId }) => {
        const selectedNode = get(groupedFlatTreeDataByValue, `${selectedId}[0]`)

        const areAllChildrenSelected =
          isEmpty(selectedNode?.children) ||
          !some(
            selectedNode.children,
            child =>
              !some(
                value,
                ({ value: selectedValueId }) => selectedValueId === child.value,
              ),
          )

        return {
          [selectedId]: areAllChildrenSelected,
        }
      }),
    )
  }, [
    areMissingChildrenTagsHighlighted,
    groupedFlatTreeDataByValue,
    treeCheckable,
    value,
  ])

  const tagRender = useCallback(
    props => (
      <LazyTreeSelectTag
        {...props}
        isAllChildrenSelected={get(
          nodeIdsAllChildrenSelectedStatus,
          props.value,
          true,
        )}
        onFocus={handleOnFocusTreeSelectItem}
        groupedFlatTreeDataByValue={groupedFlatTreeDataByValue}
      />
    ),
    [
      nodeIdsAllChildrenSelectedStatus,
      handleOnFocusTreeSelectItem,
      groupedFlatTreeDataByValue,
    ],
  )

  const handleOnBlur = useCallback(() => setSearchValue(), [])
  const previousSearchValue = usePrevious(searchValue)

  useEffect(() => {
    if (previousSearchValue !== searchValue) {
      handleDebouncedFilterSearchableTree(searchValue)
    }
  }, [searchValue, handleDebouncedFilterSearchableTree, previousSearchValue])

  const previousTreeDefaultExpandedKeys = usePrevious(treeDefaultExpandedKeys)

  useEffect(() => {
    if (
      !isEqual(
        treeDefaultExpandedKeys?.sort(),
        previousTreeDefaultExpandedKeys?.sort(),
      )
    ) {
      setExpandedKeys(treeDefaultExpandedKeys)
    }
  }, [treeDefaultExpandedKeys, previousTreeDefaultExpandedKeys])

  useEffect(() => setSearchableTreeData(lazyTreeData), [lazyTreeData])

  return (
    <AntTreeSelect
      onTreeExpand={handleExpandKeys}
      treeExpandedKeys={expandedKeys}
      treeNodeLabelProp={treeNodeLabelProp}
      onChange={handleOnChange}
      value={value}
      onSearch={handleOnSearch}
      searchValue={searchValue}
      tagRender={tagRender}
      onBlur={handleOnBlur}
      treeCheckable={treeCheckable}
      dropdownRender={handleTreeSelectDropdownSearchHighlights}
      treeData={searchableTreeDataToUse}
      treeNodeFilterProp="title"
      dropdownMatchSelectWidth={SIZES.DROPDOWN_MIN_WIDTH}
      onFocus={handleOnFocusTreeSelectItem}
      {...rest}
    />
  )
}

LazyTreeSelect.propTypes = {
  treeNodeLabelProp: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        title: PropTypes.string,
      }),
    ),
  ]),
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  name: antFormItemNamePropType.isRequired,
  treeData: PropTypes.array.isRequired,
  treeCheckable: PropTypes.bool,
  setListSize: PropTypes.func,
  treeDefaultExpandedKeys: PropTypes.arrayOf(PropTypes.string),
  flatTreeData: PropTypes.arrayOf(PropTypes.object),
  groupedFlatTreeDataByValue: PropTypes.object,
  initialValue: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        title: PropTypes.string,
      }),
    ),
    PropTypes.string.isRequired,
  ]),
  areMissingChildrenTagsHighlighted: PropTypes.bool,
}

LazyTreeSelect.defaultProps = {
  value: undefined,
  onChange: noop,
  onFocus: undefined,
  treeCheckable: false,
  treeNodeLabelProp: 'title',
  setListSize: undefined,
  treeDefaultExpandedKeys: [],
  flatTreeData: [],
  groupedFlatTreeDataByValue: {},
  initialValue: [],
  areMissingChildrenTagsHighlighted: false,
}

export default LazyTreeSelect
